import React, { FC, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Button, Modal,
    ModalBody as Body,
    ModalFooter as Footer,
    ModalHeader as Header,
    ModalTitle as Title
} from "react-bootstrap";
import GroupedAction from "Entity/Collection/ListingSetting/GroupedAction";
import LoadingButton from "../../Button/LoadingButton";
import Collection from "Config/Collection";
import Property from "../../../Entity/Property";
import { useNavigate } from "react-router-dom";
import FormEmpty from "../../../Vendor/Components/FormEmpty";
import { classUtils as c } from "../../../Vendor/Utils/ClassUtils";
import { UserContext } from "../../../Vendor/Context/UserContextProvider";
import Api from 'Vendor/Api';
import ChoiceType, { Choice } from "Vendor/Components/Form/Type/ChoiceType";

export interface _ShoppingModalProps {
    collection?: Collection;
    showActionModals: { [key: string]: boolean | Property };
    handleShowActionModal: (groupedAction?: GroupedAction, actionFromMain?: boolean) => void;
    fromMain: boolean;
    handleShowMainActionModal: () => void;
    selected: number[];
    actionModalFormIsLoading: boolean;
    handleActionModalOnResponse: () => void;
    handleActionModalOnSuccess: () => void;
    handleActionModalOnSubmit: () => void;
    actionModalFormIsLoaded: boolean;
    setActionModalFormIsLoaded: (isLoaded: boolean) => void;
    shoppingActionType: ShoppingActionModal;
    setShoppingActionType: (actionType: ShoppingActionModal) => void;
};

export type ShoppingActionModal =
    | undefined
    | 'shopping' | 'preOrder'
    | 'createNewShopping' | 'addToExistingShopping'
    | 'createNewPreOrder' | 'addToExistingPreOrder';

const _ShoppingModal: FC<_ShoppingModalProps> = ({
    collection,
    handleShowActionModal,
    fromMain,
    handleShowMainActionModal,
    selected,
    actionModalFormIsLoading,
    setActionModalFormIsLoaded,
    handleActionModalOnResponse,
    handleActionModalOnSuccess,
    handleActionModalOnSubmit,
    actionModalFormIsLoaded,
    showActionModals,
    shoppingActionType,
    setShoppingActionType,
}) => {
    const { t } = useTranslation();
    const { currentUser } = useContext(UserContext);
    const navigate = useNavigate();
    const [additionalData, setAdditionalData] = useState<undefined | object>(undefined);
    const [availableChoices, setAvailableChoices] = useState<Choice[]>([]);
    const [isLoadingChoices, setIsLoadingChoices] = useState(false);
    const [selectedChoice, setSelectedChoice] = useState<string>('');

    const onSubmit = () => {
        handleActionModalOnSubmit();
    };

    useEffect(() => {
        if (collection === 'catalog') {
            setAdditionalData({
                products: selected,
            });
        } else {
            setAdditionalData(undefined);
        }
    }, [collection, selected]);

    const onSuccess = (data: any) => {
        if (shoppingActionType) {
            let page = shoppingActionType.includes('PreOrder') ? 'pre-order' : 'shopping';
            navigate(`/${page}`, {
                state: {
                    shopping: data.id,
                },
            });
        }
    };

    const onHide = () => {
        handleShowActionModal();
        setShoppingActionType(undefined);
        setSelectedChoice('');
        setAvailableChoices([]);
    };

    const handleDrillDownClick = (type: 'shopping' | 'preOrder') => {
        setShoppingActionType(type);
    };

    const fetchAvailableChoices = (type: 'shopping' | 'preOrder') => {
        setIsLoadingChoices(true);

        const endpoint = type === 'shopping' ? '/shopping/shopping' : '/shopping/pre-order';

        const payload = {
            currentPage: 1,
            nbEntries: 50,
            keyword: '',
            filters: {
                status: ['draft', 'validated']
            }
        };

        Api.post(endpoint, payload)
            .then((response) => {
                setAvailableChoices(mapToChoices(response.data, type));
            })
            .finally(() => setIsLoadingChoices(false));
    };

    const mapToChoices = (data: any, type: 'shopping' | 'preOrder'): Choice[] => {
        const typeLabel = type === 'shopping' ? 'Shopping' : 'Pre‑order';

        return (data.items || []).map((item: any) => ({
            i18nKey: `${typeLabel} #${item.id}`,
            value: String(item.id),
        }));
    };

    useEffect(() => {
        if (shoppingActionType === 'addToExistingShopping') {
            fetchAvailableChoices('shopping');
        } else if (shoppingActionType === 'addToExistingPreOrder') {
            fetchAvailableChoices('preOrder');
        } else {
            setAvailableChoices([]);
            setSelectedChoice('');
        }
    }, [shoppingActionType]);

    const handleChoiceChange = (value: string | string[]) => {
        if (typeof value === 'string') {
            setSelectedChoice(value);
        }
    };

    const shouldRenderFormEmpty = () => {
        return shoppingActionType === 'createNewShopping' || shoppingActionType === 'createNewPreOrder';
    };

    const shouldRenderChoiceType = () => {
        return shoppingActionType === 'addToExistingShopping' || shoppingActionType === 'addToExistingPreOrder';
    };

    function getShoppingTranslationKey(actionType: ShoppingActionModal): string {
        switch (actionType) {
            case 'shopping':
                return 'shopping';
            case 'preOrder':
                return 'preOrder';
            case 'createNewShopping':
                return 'new_shopping';
            case 'createNewPreOrder':
                return 'new_preOrder';
            case 'addToExistingShopping':
                return 'add_to_shopping';
            case 'addToExistingPreOrder':
                return 'add_to_preOrder';
            default:
                return 'shopping';
        }
    };

    const addProductsToShopping = (shoppingId: number, productIds: number[]) => {
        handleActionModalOnSubmit();
        Api.post(`/shopping/${shoppingId}/add-products`, {
            products: productIds,
        })
            .then((response) => {
                handleActionModalOnSuccess();
                onSuccess(response);
            })
            .catch(() => { })
            .then(() => {
                handleActionModalOnResponse();
            });
    };

    return (
        <Modal show={showActionModals.shopping !== false} onHide={onHide} enforceFocus={false}>
            <Header closeButton={!fromMain}>
                <div className="modal-block-header">
                    {fromMain && (
                        <i
                            className="fa-solid fa-angle-left"
                            onClick={() => {
                                if (
                                    [
                                        'createNewShopping',
                                        'addToExistingShopping',
                                        'createNewPreOrder',
                                        'addToExistingPreOrder',
                                    ].includes(shoppingActionType!)
                                ) {
                                    setShoppingActionType(
                                        shoppingActionType!.includes('PreOrder') ? 'preOrder' : 'shopping'
                                    );
                                } else if (shoppingActionType !== undefined) {
                                    setShoppingActionType(undefined);
                                } else {
                                    handleShowMainActionModal();
                                }
                            }}
                        ></i>
                    )}
                    <Title>
                        {t(
                            'app.groupedActionChoice.shopping.' +
                            getShoppingTranslationKey(shoppingActionType),
                            { count: selected.length }
                        )}
                    </Title>
                </div>
            </Header>
            <Body>
                {shoppingActionType === undefined ? (
                    <ul className="grouped-action-choices">
                        {currentUser?.currentOrganization?.options.includes('shopping') &&
                            currentUser.currentOrganization?.setting.shoppingTypes.includes('shopping') && (
                                <li
                                    onClick={() => handleDrillDownClick('shopping')}
                                    className={c('btn btn-secondary', selected.length === 0 && 'disabled')}
                                >
                                    <span>
                                        <i className="fa-solid fa-bag-shopping icon"></i>
                                        {t('app.shopping')}
                                    </span>
                                    <i className="action-icon"></i>
                                </li>
                            )}
                        {currentUser?.currentOrganization?.setting.shoppingTypes.includes('pre-order') && (
                            <li
                                onClick={() => handleDrillDownClick('preOrder')}
                                className={c('btn btn-secondary', selected.length === 0 && 'disabled')}
                            >
                                <span>
                                    <i className="fa-solid fa-list-check icon"></i>
                                    {t('app.pre_order')}
                                </span>
                                <i className="action-icon"></i>
                            </li>
                        )}
                    </ul>
                ) : ['shopping', 'preOrder'].includes(shoppingActionType) ? (
                    <ul className="grouped-action-choices">
                        <li
                            onClick={() =>
                                setShoppingActionType(
                                    shoppingActionType === 'shopping' ? 'createNewShopping' : 'createNewPreOrder'
                                )
                            }
                            className={c('btn btn-secondary', selected.length === 0 && 'disabled')}
                        >
                            <span>
                                <i className="fa-solid fa-plus icon"></i>
                                {t(
                                    `app.groupedActionChoice.shopping.${shoppingActionType === 'shopping'
                                        ? 'new_shopping'
                                        : 'new_preOrder'
                                    }`,
                                    { count: selected.length }
                                )}
                            </span>
                            <i className="action-icon"></i>
                        </li>
                        <li
                            onClick={() => {
                                const target =
                                    shoppingActionType === 'shopping'
                                        ? 'addToExistingShopping'
                                        : 'addToExistingPreOrder';
                                setShoppingActionType(target);
                            }}
                            className={c('btn btn-secondary', selected.length === 0 && 'disabled')}
                        >
                            <span>
                                <i className="fa-solid fa-arrow-up-right-from-square icon"></i>
                                {t(
                                    `app.groupedActionChoice.shopping.${shoppingActionType === 'shopping'
                                        ? 'add_to_shopping'
                                        : 'add_to_preOrder'
                                    }`,
                                    { count: selected.length }
                                )}
                            </span>
                            <i className="action-icon"></i>
                        </li>
                    </ul>
                ) : shouldRenderFormEmpty() ? (
                    <FormEmpty
                        path={
                            shoppingActionType === 'createNewShopping'
                                ? '/shopping/shopping/new?groupedAction=1'
                                : '/shopping/pre-order/new?groupedAction=1'
                        }
                        method="post"
                        additionalData={additionalData}
                        setIsLoaded={setActionModalFormIsLoaded}
                        onResponse={handleActionModalOnResponse}
                        onSubmit={onSubmit}
                        onSuccess={onSuccess}
                    >
                        <div className="row justify-content-end align-items-center mb-3">
                            <div className="col-auto">
                                <LoadingButton
                                    type="submit"
                                    isLoading={actionModalFormIsLoading}
                                    disabled={!actionModalFormIsLoaded}
                                >
                                    {t('app.add')}
                                </LoadingButton>
                            </div>
                        </div>
                    </FormEmpty>
                ) : shouldRenderChoiceType() ? (
                    <form>
                        <ChoiceType
                            id={`${shoppingActionType}-select`}
                            name="shoppingChoice"
                            choices={availableChoices}
                            value={selectedChoice}
                            setValue={handleChoiceChange}
                            i18nLabel={
                                shoppingActionType === 'addToExistingShopping'
                                    ? 'app.shopping'
                                    : 'app.pre_order'
                            }
                            i18nPlaceholder={
                                shoppingActionType === 'addToExistingShopping'
                                    ? 'app.select_shopping'
                                    : 'app.select_pre_order'
                            }
                            required={true}
                            disabled={isLoadingChoices}
                            className={isLoadingChoices ? 'loading form-select' : 'form-select'}
                        />
                        <div className="row justify-content-end align-items-center mb-3">
                            <div className="col-auto">
                                <LoadingButton
                                    type="button"
                                    isLoading={actionModalFormIsLoading}
                                    disabled={!selectedChoice || isLoadingChoices}
                                    onClick={() => {
                                        addProductsToShopping(parseInt(selectedChoice, 10), selected);
                                    }}
                                >
                                    {t('app.add')}
                                </LoadingButton>
                            </div>
                        </div>
                    </form>
                ) : null}
            </Body>
            {!fromMain && (
                <Footer>
                    <Button variant="secondary" onClick={onHide}>
                        {t('app.cancel')}
                    </Button>
                </Footer>
            )}
        </Modal>
    );
};

export default _ShoppingModal;
